"use client";

import { ConfirmationContent } from "@/app/_components/ConfirmationPayment";
import { PricingPlans } from "@/app/_components/PricingPlans";
import { createClient } from "@/prismicio";
import { useCallback, useEffect, useMemo, useState } from "react";
import cn from "@/utils/cn";
import Image from "next/image";
import { useZStore } from "@/utils/z-store";
import CloseIcon from "../../_components/modals/assets/close-icon.svg";
import { PaymentLayout } from "@/app/_components/PaymentLayout";
import TextComponent from "@/app/_components/TextComponent";
import { trackGAEvent } from "@/utils/ga";

type Step = "selection" | "confirmation";

interface OrderDetails {
  orderNumber: string;
  email: string;
  subscriptionId?: string;
  activationDate?: string;
  planName?: string;
  price?: number;
  totalPaid?: number;
  discounts?: any[];
}

const usePricingData = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const client = createClient();
        const content = await client.getSingle("pricing_page");
        const pricingSlice = content.data.slices.find(
          (slice: any) => slice.slice_type === "pricing",
        );
        setData(pricingSlice?.items || []);
      } catch (err) {
        setError("Failed to fetch pricing data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  return { data, loading, error };
};

export default function BillingModalContent() {
  const [step, setStep] = useState<Step>("selection");
  const [selectedPlan, setSelectedPlan] = useState<string>("Standard_MONTHLY");
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [showFeatureMatrix, setShowFeatureMatrix] = useState(true);
  const [promoCode] = useState<string | undefined>(undefined);
  const [appliedCoupon] = useState<any | null>(null);
  const [apiPrice, setApiPrice] = useState<number | null>(null);
  const [priceLoading, setPriceLoading] = useState<boolean>(false);
  const [priceError, setPriceError] = useState<string | null>(null);

  const { data: pricingItems, loading, error } = usePricingData();
  const paymentSuccessSessionId = useZStore(
    (state) => state.data.paymentSuccessSessionId,
  );
  const actions = useZStore((state) => state.actions);
  const subscriptionPlan = useZStore((state) => state.data.subscriptionPlan);

  // Fetch price from API when selectedPlan changes
  useEffect(() => {
    const fetchPrice = async () => {
      if (!selectedPlan) return;

      const [planName, billingCycle] = selectedPlan.split("_");

      setPriceLoading(true);
      setPriceError(null);

      try {
        const response = await fetch(
          `/api/stripe/products?planName=${planName}&billingCycle=${billingCycle}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch price");
        }

        const data = await response.json();
        setApiPrice(data.price);
      } catch (err) {
        setPriceError("Failed to fetch price from API");
        console.error("Error fetching price:", err);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrice();
  }, [selectedPlan]);

  const handlePlanSelection = useCallback(
    async (plan: string, billingCycle: "MONTHLY" | "ANNUAL") => {
      setSelectedPlan(`${plan}_${billingCycle}`);
      trackGAEvent(`continue_payment_${plan.toLowerCase()}`, {});

      // Free plan has no Stripe checkout — activate it server-side and bounce to the dashboard.
      if (plan === "BASIC") {
        try {
          const response = await fetch("/api/subscription/free", {
            method: "POST",
          });
          if (!response.ok) throw new Error("Failed to activate free plan");
          window.location.href = "/dashboard/home";
        } catch (error) {
          console.error("Error activating free plan:", error);
        }
        return;
      }

      try {
        const response = await fetch("/api/subscription", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            planName: plan,
            billingCycle: billingCycle,
            promoCode: promoCode,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to create checkout session");
        }

        const data = await response.json();

        // Redirect to Stripe Checkout
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error("No checkout URL returned");
        }
      } catch (error) {
        console.error("Error creating checkout session:", error);
        // Handle error appropriately - maybe show a toast message
      }
    },
    [promoCode],
  );

  const handlePaymentSuccess = useCallback((details: OrderDetails) => {
    // Add current date as activation date if not provided
    const orderDetailsWithActivation = {
      ...details,
      activationDate: details.activationDate || new Date().toISOString(),
    };
    setOrderDetails(orderDetailsWithActivation);
    setStep("confirmation");
  }, []);

  const planDetails = useMemo(() => {
    if (!selectedPlan || !pricingItems.length)
      return {
        name: "Standard",
        price: 252.0,
        cycle: "MONTHLY" as "MONTHLY" | "ANNUAL",
      };

    const [planName, billingCycle] = selectedPlan.split("_");

    // Map plan names to array indices (assuming order: Basic, Standard, Premium)
    const planIndex =
      planName === "BASIC" ? 0 : planName === "STANDARD" ? 1 : 2;
    const planData = pricingItems[planIndex];

    if (!planData)
      return {
        name: "Standard",
        price: 252.0,
        cycle: "MONTHLY" as "MONTHLY" | "ANNUAL",
      };

    // Extract plan name from Prismic rich text format
    const displayName = planData.title[0]?.text || planName;

    // Use API price if available, otherwise fallback to Prismic data
    const price =
      apiPrice !== null
        ? apiPrice
        : billingCycle === "ANNUAL"
          ? parseFloat(planData.annual_price[0]?.text || "0")
          : parseFloat(planData.monthly_price[0]?.text || "0");

    return {
      name: displayName,
      price: price,
      cycle: billingCycle as "MONTHLY" | "ANNUAL",
    };
  }, [selectedPlan, pricingItems, apiPrice]);

  // Listen for payment success session ID
  useEffect(() => {
    const processPaymentSuccess = async () => {
      if (paymentSuccessSessionId) {
        try {
          // Fetch session details from our API
          const response = await fetch(
            `/api/stripe/checkout-session?session_id=${paymentSuccessSessionId}`,
          );

          if (!response.ok) {
            throw new Error("Failed to fetch session details");
          }

          const sessionDetails = await response.json();

          // Create order details from session data
          const orderDetailsFromSession: OrderDetails = {
            orderNumber: `ORDER-${Date.now()}`,
            email:
              sessionDetails.customer_email ||
              sessionDetails.metadata?.userEmail,
            subscriptionId: sessionDetails.subscription as string,
            activationDate: new Date().toISOString(),
            planName:
              sessionDetails.line_items?.data?.[0]?.description ||
              planDetails.name,
            price:
              (sessionDetails.line_items?.data?.[0]?.amount_total ||
                planDetails.price) / 100,
            totalPaid:
              (sessionDetails.line_items?.data?.[0]?.amount_total ||
                planDetails.price) / 100,
            discounts: sessionDetails.discounts,
          };

          // Call handlePaymentSuccess to show confirmation
          handlePaymentSuccess(orderDetailsFromSession);

          // Clear the session ID from store
          actions.clearPaymentSuccessSessionId();
        } catch (error) {
          console.error("Error fetching session details:", error);
          // Handle error appropriately
        }
      }
    };

    processPaymentSuccess();
  }, [paymentSuccessSessionId, actions, handlePaymentSuccess]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case "selection":
        return "Upgrade Your Plan";
      case "confirmation":
        return "Confirmation";
      default:
        return "";
    }
  }, [step]);

  if (loading) return <div className="py-20 text-center">Loading...</div>;
  if (error)
    return <div className="py-20 text-center text-red-500">{error}</div>;
  if (priceError)
    return <div className="py-20 text-center text-red-500">{priceError}</div>;

  return (
    <main
      // className={cn(
      //   "relative my-10 w-[550px] lg:w-[600px] max-w-[1494px] rounded-[20px] bg-gray-50 px-[43px] py-[36px] font-['Poppins'] shadow-[0px_4px_20px_0px_rgba(142,142,147,0.50)] 2xl:w-[1494px] 2xl:px-[112px] 2xl:py-[69px] xl:w-[1110px] xl:h-[653px]",
      // )}
      className={cn(
        "relative my-10 w-[263px] md:w-[550px] lg:w-[600px] max-w-[1494px] rounded-[20px] bg-gray-50 px-[43px] py-[36px] font-['Poppins'] shadow-[0px_4px_20px_0px_rgba(142,142,147,0.50)] xl:w-[1110px] 2xl:w-[1494px] xl:px-[112px] xl:py-[69px]",
      )}
    >
      <div
        onClick={() => {
          trackGAEvent("cancel_payment_flow", { step: step });
          actions.closeModal();
        }}
        className="absolute right-4 top-4 z-10 cursor-pointer"
      >
        <Image src={CloseIcon} alt="close icon" className="hover:opacity-60" />
      </div>

      <TextComponent type="h2" className="text-kd-lte-blue">
        {stepTitle}
      </TextComponent>
      {step === "confirmation" && (
        <p className="mt-2 font-semibold text-green-500">
          Successfully activated!
        </p>
      )}

      {step === "selection" && (
        <PricingPlans
          onSelectPlan={handlePlanSelection}
          items={pricingItems}
          showFeatureMatrix={showFeatureMatrix}
          setShowFeatureMatrix={setShowFeatureMatrix}
          subscriptionPlan={subscriptionPlan}
        />
      )}

      {/* {step === "payment" && selectedPlan && (
        <PaymentLayout
          step="payment"
          planName={planDetails.name}
          price={planDetails.price}
          billingCycle={planDetails.cycle}
          onNavigateToSelection={() => setStep("selection")}
          onPromoCodeApplied={(coupon: any) => {
            setAppliedCoupon(coupon);
            setPromoCode(coupon.id);
          }}
          appliedCoupon={appliedCoupon}
        >
          {priceLoading ? (
            <div className="py-10 text-center">Loading price...</div>
          ) : (
            <PaymentForm
              plan={planDetails.name}
              billingCycle={planDetails.cycle}
              price={planDetails.price}
              onSuccess={handlePaymentSuccess}
              promoCode={promoCode}
            />
          )}
        </PaymentLayout>
      )} */}

      {step === "confirmation" && orderDetails && (
        <PaymentLayout
          step="confirmation"
          planName={orderDetails.planName || planDetails.name}
          price={orderDetails.price || planDetails.price}
          billingCycle={planDetails.cycle}
          orderNumber={orderDetails.orderNumber}
          activationDate={orderDetails.activationDate}
        >
          <ConfirmationContent
            email={orderDetails.email}
            plan={orderDetails.planName || planDetails.name}
            totalPaid={orderDetails.totalPaid}
            discounts={orderDetails.discounts}
          />
        </PaymentLayout>
      )}
    </main>
  );
}
